import React, {useCallback} from 'react';
import {bookPick, bookStock, loadStock, unbookPick} from './api';
import {fmt0, qtyNum} from './format';
import {WH_LAHR, bookQtyFor, checkKeyOf, massCand, stockCorrectionFor} from './wizard';
import {WH_OF_STAMM, pickedOf, sourceWarehouseFor} from './availability';

/** Lesbarer Name eines Lagers für Dialogtexte. */
function lagerName(warehouseId, order) {
    const code = Object.keys(WH_OF_STAMM).find((k) => WH_OF_STAMM[k] === String(warehouseId));
    if (code === 'SNV') {
        return 'Schneider';
    }
    return code || (order && order.warehouseName) || 'dem Auftragslager';
}

/**
 * Grund einer abgelehnten Buchung: der Server legt ihn bei HTTP 400 in
 * `error` bzw. `failed[0].error` ab — sonst zeigte die Meldung nur
 * „Request failed with status code 400" (Ticket 146).
 */
function fehlerGrund(e) {
    const d = (e && e.response && e.response.data) || {};
    const f = Array.isArray(d.failed) && d.failed[0];
    return d.error || (f && (f.articleNumber ? f.articleNumber + ': ' : '') + f.error) || (e && e.message) || 'unbekannt';
}

// Bestandsaktionen des Kommissionierungs-Schritts (Original `bookOnePosition`
// 3956, `unbookOnePosition` 3999, `askPosOut` 3937, `doMassAvail` 3700,
// `openMassBookModal` 3745, `doMassBook` 3773, `doBatchPick` 4019,
// `toggleStockCheck` 3613, `checkAllVisibleLahr` 3660).
// ABGLEICH E67–E70, E103, E112, E115, E121, F5–F7, G27, G28, H21, J55, J56.
//
// Alle Buchungen laufen SOFORT nach weclapp — bewusst entkoppelt von der
// Speicherleiste, damit dort nur echte Auftragsänderungen stehen. Nach jeder
// Buchung wird der Bestand frisch geholt, sonst zeigten die Ampeln den alten
// Stand.

/** Sind Reservierungen und Buchungen in Lahr gesperrt (PLAN.md 3.8)? */
export function lahrGesperrt(stock) {
    return stock.lahrStockTrusted === false;
}

export const LAHR_SPERRE_TIP = 'Lahr-Bestand in Pflege — Reservierungen erst nach Freigabe';

/**
 * Meldungen des Servers zu Positionen, die nicht (voll) reserviert werden
 * konnten: Artikelnummer, gebraucht, verfügbar, wer reserviert hält. Mehr als
 * drei werden zusammengefasst.
 */
function knappText(failed) {
    const liste = (failed || []).map((f) => f.error).filter(Boolean);
    if (!liste.length) {
        return '';
    }
    const kopf = liste.slice(0, 3).join(' ');
    return liste.length > 3 ? `${kopf} (+${liste.length - 3} weitere)` : kopf;
}

export default function usePicking({stateRef, dispatch, orderRef, toast, ask, askQty}) {
    /** Bestand nach einer Buchung frisch holen (Original: jedes Mal `/api/stock`). */
    const bestandNachziehen = useCallback(async () => {
        const s = stateRef.current;
        if (!s.order) {
            return;
        }
        try {
            const data = await loadStock(orderRef, {type: s.mode, id: s.order.orderId});
            dispatch({type: 'stock-refresh', stock: data});
        } catch (e) {
            toast('Der Bestand konnte nach der Buchung nicht neu geladen werden — die Ampeln zeigen noch den alten '
                + 'Stand.', {variant: 'danger'});
        }
    }, [stateRef, dispatch, orderRef, toast]);

    /** Vom Server tatsächlich reservierte Mengen in die Zeilen übernehmen. */
    const pickedMerken = useCallback((j) => {
        const zeilen = stateRef.current.rows;
        for (const p of (j.picked || [])) {
            const r = zeilen.find((x) => String(x.id) === String(p.salesOrderItemId));
            if (r) {
                dispatch({type: 'row-picked', id: r.id, pickedQty: pickedOf(r) + (parseFloat(p.quantity) || 0)});
            }
        }
    }, [stateRef, dispatch]);

    /**
     * Eine Position buchen (Original `bookOnePosition`, 3956).
     * `pick` sichert nur für diesen Auftrag, `in`/`out` sind echte
     * Lagerbewegungen.
     */
    const buchen = useCallback(async (index, direction, qtyOverride) => {
        const s = stateRef.current;
        const r = s.rows[index];
        if (!r || !r.articleId || !s.order) {
            return;
        }
        const qty = qtyOverride > 0 ? Math.ceil(qtyOverride) : bookQtyFor(r, direction, s.stock, s.order);
        if (!(qty > 0)) {
            toast(direction === 'out' ? 'Kein Bestand zum Senken' : 'Nichts zu buchen');
            return;
        }

        if (direction === 'pick') {
            toast(`${r.articleNumber}: reserviert …`);
            try {
                const j = await bookPick(orderRef, [{
                    salesOrderItemId: String(r.id),
                    articleId: String(r.articleId),
                    articleNumber: r.articleNumber,
                    quantity: qty,
                    warehouseId: sourceWarehouseFor(r, s.stock, s.order),
                }]);
                if (j.dryRun) {
                    toast(`Trockenlauf: ${j.message || 'Es wurde nichts reserviert.'}`);
                    return;
                }
                if (j.picked && j.picked.length) {
                    const got = (j.picked[0] && j.picked[0].quantity) || qty;
                    dispatch({type: 'row-picked', id: r.id, pickedQty: pickedOf(r) + got});
                    await bestandNachziehen();
                    if (j.ok) {
                        toast(`🔒 ${r.articleNumber}: ${fmt0(got)} reserviert — für diesen Auftrag verfügbar`);
                    } else {
                        toast(`🔒 ${r.articleNumber}: nur ${fmt0(got)} von ${fmt0(qty)} reserviert. ${knappText(j.failed)}`,
                            {variant: 'danger', autohide: false});
                    }
                    return;
                }
                toast('Reservierung fehlgeschlagen: '
                    + (j.error || (j.failed && j.failed[0] && j.failed[0].error) || 'unbekannt'),
                {variant: 'danger', autohide: false});
            } catch (e) {
                toast('Reservierung fehlgeschlagen: ' + (e.response?.data?.error || e.message),
                    {variant: 'danger', autohide: false});
            }
            return;
        }

        toast(`${r.articleNumber}: bucht ${direction === 'out' ? 'aus' : 'ein'} …`);
        try {
            const j = await bookStock(orderRef, {
                items: [{
                    articleId: String(r.articleId),
                    articleNumber: r.articleNumber,
                    quantity: qty,
                    direction,
                    // Lager der Position, nicht das Kopflager (Ticket 146).
                    warehouseId: sourceWarehouseFor(r, s.stock, s.order) || undefined,
                }],
            });
            if (j.dryRun) {
                toast(`Trockenlauf: ${j.message || 'Es wurde nichts gebucht.'}`);
                return;
            }
            if (j.ok) {
                await bestandNachziehen();
                toast(`✓ ${r.articleNumber}: ${direction === 'out' ? '−' : '+'}${fmt0(qty)} gebucht — Bestand aktualisiert`);
                return;
            }
            toast('Buchung fehlgeschlagen: '
                + (j.error || (j.failed && j.failed[0] && j.failed[0].error) || 'unbekannt'),
            {variant: 'danger', autohide: false});
        } catch (e) {
            toast('Buchung fehlgeschlagen: ' + fehlerGrund(e), {variant: 'danger', autohide: false});
        }
    }, [stateRef, dispatch, orderRef, toast, bestandNachziehen]);

    /** Reservierung einer Position aufheben (Original `unbookOnePosition`, 3999). */
    const reservierungLoesen = useCallback((index) => {
        const s = stateRef.current;
        const r = s.rows[index];
        if (!r || !r.id || !s.order) {
            return;
        }
        const had = pickedOf(r);
        if (!(had > 0)) {
            toast('Für diese Position ist nichts reserviert');
            return;
        }

        ask({
            title: 'Reservierung aufheben',
            confirmLabel: 'Aufheben',
            body: (
                <>
                    <p>
                        Reservierung für <b>{r.articleNumber}</b> ({fmt0(had)} Stück) wieder aufheben?
                    </p>
                    <p>
                        Der physische Bestand ändert sich <b>NICHT</b> — die Ware ist danach nur nicht mehr für
                        {' '}{s.order.number} gesperrt.
                    </p>
                </>
            ),
            onConfirm: async () => {
                toast(`${r.articleNumber}: Reservierung wird aufgehoben …`);
                try {
                    const j = await unbookPick(orderRef, [String(r.id)]);
                    if (j.dryRun) {
                        toast(`Trockenlauf: ${j.message || 'Es wurde nichts aufgehoben.'}`);
                        return;
                    }
                    if (j.ok) {
                        dispatch({type: 'row-picked', id: r.id, pickedQty: 0});
                        await bestandNachziehen();
                        toast(`↩ ${r.articleNumber}: Reservierung aufgehoben`);
                        return;
                    }
                    toast('Aufheben fehlgeschlagen: ' + (j.error || 'unbekannt'), {variant: 'danger', autohide: false});
                } catch (e) {
                    toast('Aufheben fehlgeschlagen: ' + (e.response?.data?.error || e.message),
                        {variant: 'danger', autohide: false});
                }
            },
        });
    }, [stateRef, dispatch, orderRef, toast, ask, bestandNachziehen]);

    /** „✕ nicht da" an einer Position (Original `askPosOut`, 3937). */
    const askPosOut = useCallback((index) => {
        const s = stateRef.current;
        const r = s.rows[index];
        if (!r || !r.articleId || !s.order) {
            return;
        }
        const qty = bookQtyFor(r, 'out', s.stock, s.order);
        if (!(qty > 0)) {
            toast('Kein Bestand zum Senken');
            return;
        }

        ask({
            title: 'Bestand senken',
            confirmLabel: `${fmt0(qty)} ausbuchen`,
            tone: 'danger',
            body: (
                <>
                    <p className="weg">
                        ⚠ Bucht <b>{fmt0(qty)}</b> Stück per Warenausgang ohne Bezug aus{' '}
                        <b>{lagerName(sourceWarehouseFor(r, s.stock, s.order), s.order)}</b> aus.
                    </p>
                    <p>
                        Echte Lagerbewegung (erscheint im Wochenreport). Der Bestand wird nur so weit gesenkt, dass
                        die <b>anderen offenen Aufträge gedeckt bleiben</b> — diese Position geht leer aus. Nur
                        bestätigen, wenn die Ware physisch wirklich nicht da ist.
                    </p>
                    <p className="neu"><b>{r.articleNumber}</b> · {r.title || ''}</p>
                    <p className="muted">Auszubuchen: {fmt0(qty)} Stück</p>
                </>
            ),
            onConfirm: () => buchen(index, 'out'),
        });
    }, [stateRef, toast, ask, buchen]);

    /**
     * Bestandskorrektur beim Abhaken einer reduzierten Menge (Original
     * `openStockCorrectModal`, 3803).
     */
    const askStockCorrect = useCallback((index, c) => {
        const s = stateRef.current;
        const r = s.rows[index];
        if (!r || !s.order) {
            return;
        }

        askQty({
            title: 'Bestand korrigieren',
            confirmLabel: 'Bestand korrigieren',
            tone: 'danger',
            hint: (
                <>
                    Menge von <b>{fmt0(c.orig)}</b> auf <b>{fmt0(c.found)}</b> reduziert — laut weclapp liegen aber
                    noch <b>{fmt0(c.phys)}</b> Stück im Lager. Soll der Bestand auf die vorgefundene Menge korrigiert
                    werden? Bucht einen <b>Warenausgang ohne Bezug</b> (Notiz „{s.order.number}") — sofort live in
                    weclapp, erscheint im Wochenreport zur Teilinventur.{' '}
                    <span className="muted">
                        Falls ein Teil davon für andere Aufträge schon gepackt/beiseitegestellt ist, Korrekturmenge
                        unten anpassen.
                    </span>
                </>
            ),
            item: <><b>{r.articleNumber}</b> · {r.title || ''}</>,
            label: 'Korrektur: −',
            unit: 'Stk',
            inputTip: 'Menge, die aus dem Lahr-Bestand ausgebucht wird.',
            value: c.out,
            min: 1,
            max: c.phys,
            preview: (q) => (q > 0 ? `(Bestand ${fmt0(c.phys)} → ${fmt0(Math.max(0, c.phys - q))})` : ''),
            onConfirm: (q) => buchen(index, 'out', q),
        });
    }, [stateRef, askQty, buchen]);

    /**
     * Prüf-Haken umschalten (Original `toggleStockCheck`, 3613). Beim Abhaken
     * einer reduzierten Menge wird die Bestandskorrektur angeboten; der Haken
     * bleibt davon unabhängig.
     */
    const toggleCheck = useCallback((index) => {
        const s = stateRef.current;
        const r = s.rows[index];
        if (!r) {
            return;
        }
        const key = checkKeyOf(r);
        const einschalten = !s.wizard.checked.includes(key);
        dispatch({type: 'wiz-toggle-check', key});

        if (einschalten) {
            const c = stockCorrectionFor(r, s.stock, s.order);
            if (c) {
                askStockCorrect(index, c);
            }
        }
    }, [stateRef, dispatch, askStockCorrect]);

    /**
     * Sammel-Haken über alle sichtbaren Lahr-Positionen (Original
     * `checkAllVisibleLahr`, 3660). Kein Korrektur-Dialog — der kommt nur beim
     * einzelnen Abhaken.
     */
    const checkAllLahr = useCallback((indices) => {
        const s = stateRef.current;
        if (!indices.length) {
            return;
        }
        const keys = indices.map((i) => checkKeyOf(s.rows[i]));
        const alleGesetzt = keys.every((k) => s.wizard.checked.includes(k));
        const checked = alleGesetzt
            ? s.wizard.checked.filter((k) => !keys.includes(k))
            : Array.from(new Set(s.wizard.checked.concat(keys)));

        dispatch({type: 'wiz-set-checked', checked});
        toast(alleGesetzt
            ? `${indices.length} Lahr-Position(en) wieder als ungeprüft markiert`
            : `✓ ${indices.length} Lahr-Position(en) als geprüft markiert`);
    }, [stateRef, dispatch, toast]);

    /** Massenaktionen im Spaltenkopf (Original `doMassAvail`, 3700). */
    const massAvail = useCallback((dir, indices) => {
        const s = stateRef.current;
        const idx = indices.filter((i) => massCand(s.rows[i], dir, s.stock, s.order));
        if (!idx.length) {
            toast('Nichts zu tun — keine passenden sichtbaren Lahr-Positionen');
            return;
        }

        if (dir === 'pick') {
            const items = idx.map((i) => {
                const r = s.rows[i];
                return {
                    salesOrderItemId: String(r.id),
                    articleId: String(r.articleId),
                    articleNumber: r.articleNumber,
                    quantity: Math.ceil(qtyNum(r.quantity) - qtyNum(r.shippedQuantity) - pickedOf(r)),
                    warehouseId: sourceWarehouseFor(r, s.stock, s.order),
                };
            });

            ask({
                title: 'Sichtbare Lahr-Positionen reservieren',
                confirmLabel: 'Reservieren',
                body: (
                    <>
                        <p>
                            <b>{idx.length}</b> sichtbare Lahr-Position(en) werden für diesen Auftrag reserviert
                            (Entnahme).
                        </p>
                        <p>
                            Der physische Bestand ändert sich <b>NICHT</b> — die Ware ist danach nur für
                            {' '}{s.order.number} gesperrt.
                        </p>
                    </>
                ),
                onConfirm: async () => {
                    toast(`🔒 ${idx.length} Position(en) werden reserviert …`);
                    try {
                        const j = await bookPick(orderRef, items);
                        if (j.dryRun) {
                            toast(`Trockenlauf: ${j.message || 'Es wurde nichts reserviert.'}`);
                            return;
                        }
                        if (j.picked && j.picked.length) {
                            pickedMerken(j);
                            const fehl = (j.failed || []).length;
                            toast(`🔒 ${j.picked.length} reserviert`
                                + (fehl ? `, ${fehl} nicht oder nur teilweise — ${knappText(j.failed)}` : ''),
                            fehl ? {variant: 'danger', autohide: false} : undefined);
                            await bestandNachziehen();
                            return;
                        }
                        toast('Reservierung fehlgeschlagen: '
                            + (j.error || (j.failed && j.failed[0] && j.failed[0].error) || 'unbekannt'),
                        {variant: 'danger', autohide: false});
                    } catch (e) {
                        toast('Reservierung fehlgeschlagen: ' + (e.response?.data?.error || e.message),
                            {variant: 'danger', autohide: false});
                    }
                },
            });
            return;
        }

        if (dir === 'unpick') {
            const itemIds = idx.map((i) => String(s.rows[i].id));
            ask({
                title: 'Reservierungen aufheben',
                confirmLabel: 'Aufheben',
                body: (
                    <>
                        <p>
                            Reservierung von <b>{idx.length}</b> sichtbaren Lahr-Position(en) wieder aufheben?
                        </p>
                        <p>
                            Der physische Bestand ändert sich <b>NICHT</b> — die Ware ist danach nur nicht mehr für
                            {' '}{s.order.number} gesperrt.
                        </p>
                    </>
                ),
                onConfirm: async () => {
                    toast(`↩ ${idx.length} Reservierung(en) werden aufgehoben …`);
                    try {
                        const j = await unbookPick(orderRef, itemIds);
                        if (j.dryRun) {
                            toast(`Trockenlauf: ${j.message || 'Es wurde nichts aufgehoben.'}`);
                            return;
                        }
                        if (j.ok) {
                            toast(`↩ ${(j.cleared || []).length} Reservierung(en) aufgehoben`);
                            for (const i of idx) {
                                dispatch({type: 'row-picked', id: s.rows[i].id, pickedQty: 0});
                            }
                            await bestandNachziehen();
                            return;
                        }
                        toast('Aufheben fehlgeschlagen: ' + (j.error || 'unbekannt'),
                            {variant: 'danger', autohide: false});
                    } catch (e) {
                        toast('Aufheben fehlgeschlagen: ' + (e.response?.data?.error || e.message),
                            {variant: 'danger', autohide: false});
                    }
                },
            });
            return;
        }

        // „alle da" / „alle weg" sind echte Lagerbewegungen: Positionsliste
        // zeigen und ausdrücklich bestätigen lassen (Original 3745).
        const items = idx.map((i) => {
            const r = s.rows[i];
            return {
                articleId: String(r.articleId),
                articleNumber: r.articleNumber,
                title: r.title || '',
                quantity: bookQtyFor(r, dir, s.stock, s.order),
                direction: dir,
                warehouseId: sourceWarehouseFor(r, s.stock, s.order) || undefined,
            };
        }).filter((x) => x.quantity > 0);

        if (!items.length) {
            toast('Nichts zu buchen');
            return;
        }

        const isOut = dir === 'out';
        const gesamt = items.reduce((sum, x) => sum + x.quantity, 0);

        ask({
            title: isOut ? 'Bestand senken (Massenaktion)' : 'Bestand buchen (Massenaktion)',
            confirmLabel: isOut
                ? `${items.length} Position(en) ausbuchen`
                : `${items.length} Position(en) einbuchen`,
            tone: 'danger',
            body: (
                <>
                    <p className={isOut ? 'weg' : 'chg'}>
                        {isOut
                            ? <>⚠ Warenausgang ohne Bezug — senkt den Bestand von <b>{items.length}</b> Position(en)
                                {' '}({fmt0(gesamt)} Stück gesamt).</>
                            : <>⚠ Wareneingang ohne Bezug — erhöht den Bestand von <b>{items.length}</b> Position(en)
                                {' '}({fmt0(gesamt)} Stück gesamt).</>}
                    </p>
                    <p>
                        Echte Lagerbewegung im Lager der jeweiligen Position (erscheint im Wochenreport).{' '}
                        {isOut
                            ? <>Nur bestätigen, wenn die Ware physisch wirklich <b>nicht</b> da ist.</>
                            : <>Nur bestätigen, wenn die Ware physisch wirklich <b>vorhanden</b> ist.</>}
                    </p>
                    <div className="scrolllist">
                        {items.map((x) => (
                            <div key={x.articleId}>
                                {x.articleNumber} · {x.title} — {isOut ? '−' : '+'}{fmt0(x.quantity)} Stk
                            </div>
                        ))}
                    </div>
                </>
            ),
            onConfirm: async () => {
                toast('Bestand wird gebucht …');
                try {
                    const j = await bookStock(orderRef, {
                        items: items.map((x) => ({
                            articleId: x.articleId,
                            articleNumber: x.articleNumber,
                            quantity: x.quantity,
                            direction: x.direction,
                            warehouseId: x.warehouseId,
                        })),
                    });
                    if (j.dryRun) {
                        toast(`Trockenlauf: ${j.message || 'Es wurde nichts gebucht.'}`);
                        return;
                    }
                    if (j.ok || (j.booked && j.booked.length)) {
                        await bestandNachziehen();
                        const nf = (j.failed || []).length;
                        toast(`✓ ${(j.booked || []).length} Position(en) gebucht`
                            + (nf ? `, ${nf} fehlgeschlagen` : '') + ' — Bestand aktualisiert');
                        return;
                    }
                    toast('Buchung fehlgeschlagen: ' + (j.error || 'unbekannt'), {variant: 'danger', autohide: false});
                } catch (e) {
                    toast('Buchung fehlgeschlagen: ' + fehlerGrund(e), {variant: 'danger', autohide: false});
                }
            },
        });
    }, [stateRef, orderRef, toast, ask, dispatch, bestandNachziehen, pickedMerken]);

    /** Sammel-Reservierung aller Lahr-Positionen (Original `doBatchPick`, 4019). */
    const batchPick = useCallback((kandidaten) => {
        const s = stateRef.current;
        if (!s.order || !kandidaten.length) {
            toast('Nichts zu reservieren — es ist schon alles reserviert');
            return;
        }

        ask({
            title: 'Alle Lahr-Positionen reservieren',
            confirmLabel: 'Reservieren',
            body: (
                <>
                    <p>
                        <b>{kandidaten.length}</b> Lahr-Position(en) werden für diesen Auftrag reserviert (Entnahme
                        an der Position).
                    </p>
                    <p>
                        Der physische Bestand ändert sich <b>NICHT</b> — die Ware ist danach nur für
                        {' '}{s.order.number} gesperrt und die Lieferscheine gehen sicher durch.
                    </p>
                </>
            ),
            onConfirm: async () => {
                toast(`🔒 ${kandidaten.length} Lahr-Position(en) werden reserviert …`);
                try {
                    const items = kandidaten.map((c) => ({
                        salesOrderItemId: String(c.r.id),
                        articleId: String(c.r.articleId),
                        articleNumber: c.r.articleNumber,
                        quantity: c.qty,
                        warehouseId: sourceWarehouseFor(c.r, s.stock, s.order),
                    }));
                    const j = await bookPick(orderRef, items);
                    if (j.dryRun) {
                        toast(`Trockenlauf: ${j.message || 'Es wurde nichts reserviert.'}`);
                        return;
                    }
                    if (!(j.picked && j.picked.length)) {
                        toast('Sammel-Reservierung fehlgeschlagen: '
                            + (j.error || (j.failed && j.failed[0] && j.failed[0].error) || 'unbekannt'),
                        {variant: 'danger', autohide: false});
                        return;
                    }
                    const fehl = (j.failed || []).length;
                    toast(`🔒 ${j.picked.length} Position(en) reserviert`
                        + (fehl ? `, ${fehl} nicht oder nur teilweise — ${knappText(j.failed)}` : ''),
                    fehl ? {variant: 'danger', autohide: false} : undefined);
                    // Nur die Menge übernehmen, die der Server wirklich reserviert hat.
                    pickedMerken(j);
                    await bestandNachziehen();
                } catch (e) {
                    toast('Sammel-Reservierung fehlgeschlagen: ' + (e.response?.data?.error || e.message),
                        {variant: 'danger', autohide: false});
                }
            },
        });
    }, [stateRef, orderRef, toast, ask, dispatch, bestandNachziehen, pickedMerken]);

    return {
        buchen,
        reservierungLoesen,
        askPosOut,
        toggleCheck,
        checkAllLahr,
        massAvail,
        batchPick,
        bestandNachziehen,
    };
}

export {WH_LAHR};
